import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { Clock, FileText, Plus } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import { createWorkPackage, getProjectBySlug } from "@/api/plan";
import { canEdit, canManageMembers } from "@/components/chat/railMode";
import { SpaceMembersButton } from "@/components/plan/SpaceMembersButton";
import { DeskScreen } from "@/components/plan/DeskScreen";
import { ProgressBar } from "@/components/plan/ProgressBar";
import { ProjectScheduleStrip } from "@/components/plan/ProjectScheduleStrip";
import { StatusPill } from "@/components/plan/StatusPill";
import { LinkedIntegrations } from "@/components/plan/LinkedIntegrations";
import { barFill, humanizeStatus } from "@/components/plan/plan-status";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/plan/p/$projectSlug/")({
  component: ProjectDetail,
});

/** Compact "Mon D" date for the WP "Updated …" meta line. */
function fmtUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ProjectDetail() {
  const { projectSlug } = useParams({ from: "/_app/plan/p/$projectSlug/" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const key = ["plan", "project-by-slug", projectSlug];

  const [addOpen, setAddOpen] = useState(false);
  const [wpTitle, setWpTitle] = useState("");
  const [wpDesc, setWpDesc] = useState("");

  const detail = useQuery({
    queryKey: key,
    queryFn: () => getProjectBySlug(projectSlug),
  });

  const addWp = useMutation({
    mutationFn: (input: { title: string; description: string | null }) =>
      createWorkPackage(detail.data!.project.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ["plan", "projects"] });
      setAddOpen(false);
      setWpTitle("");
      setWpDesc("");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  useDocumentTitle([detail.data?.project.name ?? "Project", "Project"]);

  if (detail.isLoading) {
    return (
      <DeskScreen>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-24 w-full" />
      </DeskScreen>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <DeskScreen>
        <p className="text-[13px] text-[var(--color-semantic-error)]">
          {(detail.error as Error)?.message ?? "Project not found."}
        </p>
      </DeskScreen>
    );
  }

  const { project, work_packages, activity } = detail.data;
  // Page-body write gate (editor+). Viewers/commenters see read-only content.
  const mayEdit = canEdit(detail.data.my_role);
  const mayManageMembers = canManageMembers(detail.data.my_role);
  const projectId = project.id;
  const contributesTo = detail.data.contributes_to ?? [];
  const realizes = detail.data.realizes ?? [];
  const hasAlignment = contributesTo.length > 0 || realizes.length > 0;

  const wpCount = work_packages.length;
  const outcomeCount = detail.data.outcome_count ?? 0;

  const submitWp = (e: React.FormEvent) => {
    e.preventDefault();
    const t = wpTitle.trim();
    if (t && !addWp.isPending) addWp.mutate({ title: t, description: wpDesc.trim() || null });
  };

  // ── Above the sheet (desk): identity, health, alignment, schedule ──────────
  const toolbar = (
    <div className="desk-head">
      {/* Meta crumb line — identity around the canvas. */}
      <div className="crumb">
        PROJECT
        {project.owner_name && (
          <>
            {" "}
            <span className="sep">·</span> Owner {project.owner_name}
          </>
        )}{" "}
        <span className="sep">·</span> {humanizeStatus(project.status)}
      </div>

      {/* Title with status dot. */}
      <div className="obj-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h1 className="page" style={{ margin: 0 }}>
          <span
            className="proj-dot lg"
            style={{
              background: project.color ?? "var(--accent)",
              verticalAlign: "middle",
              marginRight: 10,
            }}
          />
          {project.name}
        </h1>
        {mayManageMembers && (
          <div style={{ marginLeft: "auto" }}>
            <SpaceMembersButton spaceId={projectId} subjectLabel="project" />
          </div>
        )}
      </div>

      {project.description && <p className="page-sub">{project.description}</p>}

      {/* Inline stat line — replaces the 4-tile health card. */}
      <div className="obj-summary">
        <span className="obj-sum-pct">{project.progress}% Complete</span>
        <span className="obj-sum-dot" />
        <span className="obj-sum-counts">
          {wpCount} Work Package{wpCount === 1 ? "" : "s"} · {outcomeCount} Outcome
          {outcomeCount === 1 ? "" : "s"}
        </span>
        {project.estimate_hours > 0 && (
          <>
            <span className="obj-sum-dot" />
            <span
              className="obj-sum-est"
              title="Total estimated effort — auto-rolled up from task estimates"
            >
              <Clock className="size-3" /> {project.estimate_hours}h estimated
            </span>
          </>
        )}
        <StatusPill status={project.status} />
      </div>

      {/* Strategic alignment — KRs this project feeds (per-link weight) + the
          initiative it grew out of. One row to keep the header calm. */}
      {hasAlignment && (
        <div className="contributes-row">
          <span className="contributes-label">Alignment</span>
          {contributesTo.slice(0, 3).map((c) => (
            <span
              key={c.kr_id}
              className="contributes-chip"
              title={`Contributes to ${c.kr_name} · weight ${c.weight}%`}
            >
              {c.kr_name} <span className="contributes-w">· {c.weight}%</span>
            </span>
          ))}
          {contributesTo.length > 3 && (
            <span
              className="contributes-via"
              title={contributesTo
                .slice(3)
                .map((c) => c.kr_name)
                .join(", ")}
            >
              +{contributesTo.length - 3} more
            </span>
          )}
          {realizes.map((it) => (
            <span
              key={it.initiative_id}
              className="contributes-chip realizes"
              title={`Realizes initiative · ${it.name}`}
            >
              <span className="realizes-ico">✦</span> {it.name}
            </span>
          ))}
        </div>
      )}

      {/* Timeline — read-only schedule strip across the work packages. */}
      <ProjectScheduleStrip
        workPackages={work_packages}
        onOpen={(wpId) =>
          navigate({ to: "/plan/p/$projectSlug/wp/$wpId", params: { projectSlug, wpId } })
        }
      />
    </div>
  );

  // ── Below the sheet (desk): connected tools, activity, discussion ──────────
  const footer = (
    <>
      <LinkedIntegrations objectKind="project" objectId={projectId} canEdit={mayEdit} />

      {activity.length > 0 && (
        <div className="section">
          <h2 className="sec" style={{ margin: "0 0 6px" }}>
            Activity
          </h2>
          <div>
            {activity.map((a) => (
              <div key={a.id} className="act-row">
                <span className="act-ico t-artifact">◆</span>
                <span>
                  <span style={{ color: "var(--ink)" }}>{a.actor_agent ?? "Someone"}</span>{" "}
                  {a.verb}{" "}
                  {a.target ? <span style={{ fontWeight: 500 }}>{a.target}</span> : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  // ── On the sheet (canvas is sacred): only the work packages ────────────────
  return (
    <DeskScreen toolbar={toolbar} footer={footer}>
      {/* data-tour anchor: the demo guided tour spotlights the project home's
          work-package list — the generic "@plan-project" landing surface
          (the WP detail page is one level too deep to reach without ids). */}
      <div data-tour="plan-project-page">
        <div className="kr-head-row">
          <h2 className="sec" style={{ margin: 0 }}>
            Work packages{" "}
            <span style={{ fontWeight: 400, color: "var(--ink-faint)" }}>({wpCount})</span>
          </h2>
          {mayEdit && (
            <button
              type="button"
              className="btn sm"
              style={{ marginLeft: "auto" }}
              onClick={() => setAddOpen(true)}
            >
              <Plus className="size-3.5" /> Add
            </button>
          )}
        </div>

        <div className="kr-list">
          {wpCount === 0 && (
            <div className="plan-empty">
              <div>
                <div className="plan-empty-h">No work packages yet</div>
                <div className="plan-empty-d">
                  Work packages break this project into outcome-driven units of work.
                </div>
              </div>
            </div>
          )}

          {work_packages.map(({ work_package: wp, health_label }, i) => (
            <Link
              key={wp.id}
              to="/plan/p/$projectSlug/wp/$wpId"
              params={{ projectSlug, wpId: wp.id }}
              className="kr-card kr-card-click"
              style={{ display: "block", textDecoration: "none", color: "inherit" }}
            >
              <div className="kr-card-top">
                <div className="kr-title">
                  <span className="ref-id">WP-{i + 1}</span>
                  {wp.title}
                </div>
              </div>
              {wp.description && <div className="kr-card-desc">{wp.description}</div>}
              <div className="kr-progress">
                <div className="kr-prog-nums">
                  <span>
                    <b>{wp.outcome_count ?? 0}</b> Outcome{(wp.outcome_count ?? 0) === 1 ? "" : "s"}
                  </span>
                  <span className="kr-pct">{wp.progress}%</span>
                </div>
                <ProgressBar value={wp.progress} variant="kr" tone={barFill(wp.progress)} />
              </div>
              <div className="kr-meta-row">
                <span className="kr-contrib-summary">
                  <FileText className="size-3.5" /> Updated {fmtUpdated(wp.updated_at)}
                </span>
                {(wp.estimate_hours ?? 0) > 0 && (
                  <span className="kr-est" title="Estimated effort — rolled up from tasks">
                    <Clock className="size-3" /> {wp.estimate_hours}h
                  </span>
                )}
                <StatusPill status={health_label} />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <div className="plan-skin">
            <DialogHeader>
              <DialogTitle>New work package</DialogTitle>
              <DialogDescription>
                An outcome-driven unit of work inside {project.name}.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitWp} className="mt-2 flex flex-col gap-4">
              <div className="field">
                <span className="field-row-label">Title</span>
                <input
                  className="input"
                  autoFocus
                  value={wpTitle}
                  onChange={(e) => setWpTitle(e.target.value)}
                  placeholder="e.g. Design phase"
                />
              </div>
              <div className="field">
                <span className="field-row-label">Description</span>
                <textarea
                  className="input"
                  rows={3}
                  value={wpDesc}
                  onChange={(e) => setWpDesc(e.target.value)}
                  placeholder="Optional — what this work package covers."
                />
              </div>
              <DialogFooter>
                <button type="button" className="btn" onClick={() => setAddOpen(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={!wpTitle.trim() || addWp.isPending}
                >
                  {addWp.isPending ? "Adding…" : "Add work package"}
                </button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </DeskScreen>
  );
}
